import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  TagIcon,
  PhotoIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  StarIcon,
  EyeIcon as EyeIconSolid,
  InformationCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi, MarketplaceListing } from '../../api/marketplace';
import { speciesApi, Species } from '../../api/species';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalConfig } from '../../contexts/GlobalConfigContext';
import { getFacilityId } from '../../utils/facilityUtils';
import toast from 'react-hot-toast';
import PhotoSelector from './PhotoSelector';
import FormattedListingModal from './FormattedListingModal';
import AmountInput from '../ui/AmountInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface ListingFilters {
  status?: string;
  category?: string;
  priceRange?: string;
  search?: string;
  platform?: string;
  species?: string;
  originLocation?: string;
  iucnStatus?: string;
  citesStatus?: string;
}

export default function ListingsManager() {
  const { user } = useAuth();
  const { config } = useGlobalConfig();
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ListingFilters>({});
  const [selectedListing, setSelectedListing] = useState<MarketplaceListing | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [speciesData, setSpeciesData] = useState<Species[]>([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showFormattedModal, setShowFormattedModal] = useState(false);
  const [selectedListingForFormat, setSelectedListingForFormat] = useState<MarketplaceListing | null>(null);

  // Use the utility function to get a proper facility ID
  const facilityId = getFacilityId(user?.facilityId);

  useEffect(() => {
    loadListings();
    loadSpeciesData();
  }, [currentPage, filters]);

  const loadSpeciesData = async () => {
    try {
      setLoadingSpecies(true);
      const response = await speciesApi.getAll();
      setSpeciesData(response.data || []);
    } catch (error) {
      console.error('Failed to load species data:', error);
    } finally {
      setLoadingSpecies(false);
    }
  };

  const loadListings = async () => {
    try {
      setLoading(true);
      const response = await marketplaceApi.getListings(facilityId, {
        page: currentPage,
        limit: 20,
        ...filters,
      });
      setListings(response.listings || []);
      setTotalPages(Math.ceil((response.total || 0) / 20));
    } catch (error) {
      console.error('Error loading listings:', error);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (listingId: string, newStatus: string) => {
    try {
      // Note: Status updates might need to be handled differently since UpdateListingDto doesn't include status
      // For now, we'll use a generic update approach
      await marketplaceApi.updateListing(facilityId, listingId, {});
      toast.success('Listing status updated successfully');
      loadListings();
    } catch (error) {
      console.error('Error updating listing status:', error);
      toast.error('Failed to update listing status');
    }
  };

  const handleDeleteListing = async (listingId: string) => {
    if (!confirm('Are you sure you want to archive this listing?')) {
      return;
    }

    try {
      // Note: API doesn't have deleteListing method, using updateListing to set status to archived
      // Since UpdateListingDto doesn't include status, we'll use a generic update
      await marketplaceApi.updateListing(facilityId, listingId, {});
      toast.success('Listing archived successfully');
      loadListings();
    } catch (error) {
      console.error('Error archiving listing:', error);
      toast.error('Failed to archive listing');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedListing) return;

    try {
      // Convert platforms to array if it's a string
      let platforms: string[] = [];
      const platformsData = selectedListing.platforms as any;
      if (typeof platformsData === 'string') {
        // Handle string format like "{whatsapp,birdMarketplace}"
        platforms = platformsData
          .replace(/[{}]/g, '') // Remove curly braces
          .split(',') // Split by comma
          .map(p => p.trim()) // Trim whitespace
          .filter(p => p.length > 0); // Remove empty strings
      } else if (Array.isArray(platformsData)) {
        platforms = platformsData;
      }

      // Prepare the update data with all fields
      const updateData = {
        title: selectedListing.title,
        description: selectedListing.description,
        price: selectedListing.price,
        specifications: selectedListing.specifications,
        highlights: selectedListing.highlights,
        status: selectedListing.status,
        platforms: platforms,
        metadata: selectedListing.metadata,
        // Include other fields that might be needed
        birdId: selectedListing.birdId,
        media: selectedListing.media,
        platformContent: selectedListing.platformContent,
      };

      await marketplaceApi.updateListing(facilityId, selectedListing.id, updateData);
      toast.success('Listing updated successfully');
      setShowEditModal(false);
      setSelectedListing(null);
      loadListings(); // Refresh the listings
    } catch (error) {
      console.error('Error updating listing:', error);
      toast.error('Failed to update listing');
    }
  };

  const formatCurrency = (price: any) => {
    const amount = typeof price === 'object' ? price.amount : price;
    const currency = typeof price === 'object' ? price.currency : (config?.currency?.default || 'ZAR');
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800';
      case 'inactive':
        return 'bg-violet-100 text-violet-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'sold':
        return 'bg-cyan-100 text-cyan-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'inactive':
        return <XCircleIcon className="h-4 w-4" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4" />;
      case 'sold':
        return <CheckCircleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const getSpeciesInfo = (listing: MarketplaceListing) => {
    // Try to find species by common name in the listing
    const speciesName = listing.specifications?.species;
    if (speciesName) {
      return speciesData.find(s => 
        s.commonName.toLowerCase() === speciesName.toLowerCase() ||
        s.genus.toLowerCase() === speciesName.toLowerCase()
      );
    }
    return null;
  };

  const getUniqueValues = (field: keyof Species) => {
    const values = speciesData.map(s => s[field]).filter(Boolean);
    return [...new Set(values)].map(value => String(value));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Listings Management</h2>
          <p className="text-sm text-slate-500">Manage your marketplace listings</p>
        </div>
        <button
          onClick={() => window.location.href = '/marketplace/new-listing'}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Listing
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900">Filters</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
                <input
                  type="text"
                  value={filters.search || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Search listings..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Species Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Species</label>
                <select
                  value={filters.species || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, species: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Species</option>
                  {speciesData.map((species) => (
                    <option key={species.id} value={species.commonName}>
                      {species.commonName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Origin Location Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Origin</label>
                <select
                  value={filters.originLocation || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, originLocation: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Origins</option>
                                     {getUniqueValues('originLocation').map((origin) => (
                     <option key={origin} value={origin}>
                       {origin}
                     </option>
                   ))}
                </select>
              </div>

              {/* IUCN Status Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">IUCN Status</label>
                <select
                  value={filters.iucnStatus || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, iucnStatus: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Statuses</option>
                  {getUniqueValues('iucnStatus').map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setFilters({})}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Listings Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg">
          {listings.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-slate-500">No listings found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {listings.map((listing) => {
                const speciesInfo = getSpeciesInfo(listing);
                return (
                  <div key={listing.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-medium text-slate-900">{listing.title}</h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(listing.status)}`}>
                            {listing.status}
                          </span>
                        </div>
                        
                        <p className="text-sm text-slate-600 mb-3">{listing.description}</p>
                        
                        {/* Species Information */}
                        {speciesInfo && (
                          <div className="mb-3 p-3 bg-cyan-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-medium text-blue-900">Species Details</h4>
                              <InformationCircleIcon className="h-4 w-4 text-cyan-600" />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              <div>
                                <span className="font-medium text-cyan-800">Scientific:</span>
                                <p className="text-cyan-700">{speciesInfo.genus} {speciesInfo.species}</p>
                              </div>
                              <div>
                                <span className="font-medium text-cyan-800">Origin:</span>
                                <p className="text-cyan-700">{speciesInfo.originLocation}</p>
                              </div>
                              <div>
                                <span className="font-medium text-cyan-800">IUCN:</span>
                                <p className="text-cyan-700">{speciesInfo.iucnStatus}</p>
                              </div>
                              <div>
                                <span className="font-medium text-cyan-800">CITES:</span>
                                <p className="text-cyan-700">{speciesInfo.citesStatus}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-slate-700">Price:</span>
                            <p className="text-slate-900">{formatCurrency(listing.price)}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Species:</span>
                            <p className="text-slate-900">{listing.specifications?.species || 'Unknown'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Age:</span>
                            <p className="text-slate-900">{listing.specifications?.age || 'Unknown'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Gender:</span>
                            <p className="text-slate-900">{listing.specifications?.gender || 'Unknown'}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setSelectedListing(listing);
                            setShowDetailsModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-cyan-600"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedListingForFormat(listing);
                            setShowFormattedModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-purple-600"
                          title="Generate Formatted Listing"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedListing(listing);
                            setShowEditModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-green-600"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteListing(listing.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Listing Details Modal */}
      <BrandModal
        open={showDetailsModal && !!selectedListing}
        onClose={() => setShowDetailsModal(false)}
        maxWidthClass="max-w-2xl"
      >
        {selectedListing ? (
          <>
            <BrandModalHeader title="Listing Details" onClose={() => setShowDetailsModal(false)} />
            <BrandModalBody>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Title</label>
                  <p className="text-sm text-slate-900">{selectedListing.title}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Price</label>
                  <p className="text-sm text-slate-900">{formatCurrency(selectedListing.price)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Category</label>
                  <p className="text-sm text-slate-900">{selectedListing.specifications?.species || 'Unknown'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Status</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedListing.status)}`}>
                    {selectedListing.status}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700">Description</label>
                <p className="text-sm text-slate-900">{selectedListing.description}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Views</label>
                  <p className="text-sm text-slate-900">{selectedListing.analytics?.views || 0}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Inquiries</label>
                  <p className="text-sm text-slate-900">{selectedListing.analytics?.inquiries || 0}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Created</label>
                  <p className="text-sm text-slate-900">
                    {new Date(selectedListing.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            </BrandModalBody>
            <BrandModalFooter>
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="btn btn-secondary text-sm"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDetailsModal(false);
                  setShowEditModal(true);
                }}
                className="btn btn-primary text-sm"
              >
                Edit Listing
              </button>
            </BrandModalFooter>
          </>
        ) : null}
      </BrandModal>

      {/* Edit Listing Modal */}
      <BrandModal
        open={showEditModal && !!selectedListing}
        onClose={() => setShowEditModal(false)}
        maxWidthClass="max-w-5xl"
      >
        {selectedListing ? (
          <>
            <BrandModalHeader title="Edit Listing" onClose={() => setShowEditModal(false)} />
            <BrandModalBody>
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-slate-50/80 p-4 rounded-lg">
                <h4 className="text-md font-medium text-slate-900 mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={selectedListing.title}
                      onChange={(e) => setSelectedListing({...selectedListing, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                    <select
                      value={selectedListing.status}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        status: e.target.value as 'draft' | 'staging' | 'published' | 'expired' | 'sold' | 'archived'
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="staging">Staging</option>
                      <option value="published">Published</option>
                      <option value="expired">Expired</option>
                      <option value="sold">Sold</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                    <textarea
                      value={selectedListing.description}
                      onChange={(e) => setSelectedListing({...selectedListing, description: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Media */}
              <div className="bg-slate-50/80 p-4 rounded-lg">
                <h4 className="text-md font-medium text-slate-900 mb-4">Media</h4>
                <PhotoSelector
                  value={selectedListing.media?.primaryImage || ''}
                  onChange={(url) => setSelectedListing({
                    ...selectedListing,
                    media: {
                      ...selectedListing.media,
                      primaryImage: url
                    }
                  })}
                  birdId={selectedListing.birdId}
                  placeholder="Select or upload a primary photo"
                />
              </div>

              {/* Price Information */}
              <div className="bg-slate-50/80 p-4 rounded-lg">
                <h4 className="text-md font-medium text-slate-900 mb-4">Pricing</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Price Amount</label>
                    <AmountInput
                      value={typeof selectedListing.price === 'object' ? selectedListing.price.amount : selectedListing.price}
                      onChange={(v) => setSelectedListing({
                        ...selectedListing, 
                        price: {
                          ...selectedListing.price,
                          amount: v ?? 0
                        }
                      })}
                      min={0}
                      inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Currency</label>
                    <select
                      value={typeof selectedListing.price === 'object' ? selectedListing.price.currency : (config?.currency?.default || 'ZAR')}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        price: {
                          ...selectedListing.price,
                          currency: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="ZAR">ZAR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={typeof selectedListing.price === 'object' ? selectedListing.price.negotiable : false}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        price: {
                          ...selectedListing.price,
                          negotiable: e.target.checked
                        }
                      })}
                      className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-slate-900">Negotiable</label>
                  </div>
                </div>
              </div>

              {/* Specifications */}
              <div className="bg-slate-50/80 p-4 rounded-lg">
                <h4 className="text-md font-medium text-slate-900 mb-4">Bird Specifications</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Species</label>
                    <input
                      type="text"
                      value={selectedListing.specifications?.species || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        specifications: {
                          ...selectedListing.specifications,
                          species: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Age</label>
                    <input
                      type="text"
                      value={selectedListing.specifications?.age || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        specifications: {
                          ...selectedListing.specifications,
                          age: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Gender</label>
                    <select
                      value={selectedListing.specifications?.gender || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        specifications: {
                          ...selectedListing.specifications,
                          gender: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Color</label>
                    <input
                      type="text"
                      value={selectedListing.specifications?.color || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        specifications: {
                          ...selectedListing.specifications,
                          color: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Size</label>
                    <select
                      value={selectedListing.specifications?.size || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        specifications: {
                          ...selectedListing.specifications,
                          size: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Size</option>
                      <option value="Small">Small</option>
                      <option value="Medium">Medium</option>
                      <option value="Large">Large</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Health Status</label>
                    <select
                      value={selectedListing.specifications?.healthStatus || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        specifications: {
                          ...selectedListing.specifications,
                          healthStatus: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Health Status</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Breeding History</label>
                    <textarea
                      value={selectedListing.specifications?.breedingHistory || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        specifications: {
                          ...selectedListing.specifications,
                          breedingHistory: e.target.value
                        }
                      })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Platforms */}
              <div className="bg-slate-50/80 p-4 rounded-lg">
                <h4 className="text-md font-medium text-slate-900 mb-4">Platforms</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {['whatsapp', 'facebook', 'instagram', 'telegram', 'birdMarketplace'].map((platform) => (
                    <div key={platform} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedListing.platforms?.includes(platform) || false}
                        onChange={(e) => {
                          const currentPlatforms = selectedListing.platforms || [];
                          const newPlatforms = e.target.checked
                            ? [...currentPlatforms, platform]
                            : currentPlatforms.filter(p => p !== platform);
                          setSelectedListing({...selectedListing, platforms: newPlatforms});
                        }}
                        className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-slate-900 capitalize">{platform}</label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Highlights */}
              <div className="bg-slate-50/80 p-4 rounded-lg">
                <h4 className="text-md font-medium text-slate-900 mb-4">Highlights</h4>
                <div className="space-y-2">
                  {selectedListing.highlights?.map((highlight, index) => (
                    <div key={index} className="flex space-x-2">
                      <input
                        type="text"
                        value={highlight}
                        onChange={(e) => {
                          const newHighlights = [...(selectedListing.highlights || [])];
                          newHighlights[index] = e.target.value;
                          setSelectedListing({...selectedListing, highlights: newHighlights});
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter highlight"
                      />
                      <button
                        onClick={() => {
                          const newHighlights = selectedListing.highlights?.filter((_, i) => i !== index) || [];
                          setSelectedListing({...selectedListing, highlights: newHighlights});
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newHighlights = [...(selectedListing.highlights || []), ''];
                      setSelectedListing({...selectedListing, highlights: newHighlights});
                    }}
                    className="px-3 py-2 text-cyan-600 hover:text-cyan-800"
                  >
                    + Add Highlight
                  </button>
                </div>
              </div>

              {/* Metadata */}
              <div className="bg-slate-50/80 p-4 rounded-lg">
                <h4 className="text-md font-medium text-slate-900 mb-4">Additional Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                    <input
                      type="text"
                      value={selectedListing.metadata?.location || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        metadata: {
                          ...selectedListing.metadata,
                          location: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Tags</label>
                    <input
                      type="text"
                      value={selectedListing.metadata?.tags?.join(', ') || ''}
                      onChange={(e) => setSelectedListing({
                        ...selectedListing, 
                        metadata: {
                          ...selectedListing.metadata,
                          tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter tags separated by commas"
                    />
                  </div>
                </div>
              </div>
            </div>
            </BrandModalBody>
            <BrandModalFooter>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="btn btn-primary text-sm"
              >
                Save Changes
              </button>
            </BrandModalFooter>
          </>
        ) : null}
      </BrandModal>

      {/* Formatted Listing Modal */}
      {showFormattedModal && selectedListingForFormat && (
        <FormattedListingModal
          isOpen={showFormattedModal}
          onClose={() => setShowFormattedModal(false)}
          listingId={selectedListingForFormat.id}
          listingTitle={selectedListingForFormat.title}
        />
      )}
    </div>
  );
}
